'use client';

import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";

function NavbarLanding() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);

    // Initialize theme based on localStorage or system preference
    useEffect(() => {
        if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
            setIsDarkMode(true);
            document.documentElement.classList.add('dark');
        } else {
            setIsDarkMode(false);
            document.documentElement.classList.remove('dark');
        }
    }, []);

    const toggleTheme = () => {
        if (isDarkMode) {
            document.documentElement.classList.remove('dark');
            localStorage.theme = 'light';
            setIsDarkMode(false);
        } else {
            document.documentElement.classList.add('dark');
            localStorage.theme = 'dark';
            setIsDarkMode(true);
        }
    };

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 20;
            setScrolled(isScrolled);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const navItems = [
        { name: 'Home', href: '/', type: 'link' },
        { name: 'Education', href: '#education', type: 'anchor' },
        { name: 'Experience', href: '#experience', type: 'anchor' },
        { name: 'Tech Stack', href: '#techstack', type: 'anchor' },
        { name: 'Portfolio', href: '#portfolio', type: 'anchor' },
        { name: 'Contact', href: '#contact', type: 'anchor' },
    ];

    return (
        <nav className={`transition-all duration-300 fixed w-full z-50 ${
            scrolled 
                ? 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-md shadow-sm border-b border-slate-200 dark:border-slate-800' 
                : 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm'
        }`}>
            <div className="container mx-auto px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    {/* Logo/Brand */}
                    <Link to="/" className="group flex items-center space-x-2">
                        <div className="relative">
                            <div className="w-10 h-10 bg-slate-900 dark:bg-slate-100 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-300 transform group-hover:scale-105">
                                <span className="text-white dark:text-slate-900 font-bold text-lg transition-colors">F</span>
                            </div>
                        </div>
                        <span className="text-2xl font-bold text-slate-900 dark:text-white transition-colors duration-300">
                            FadhlihCodes
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center space-x-2">
                        {navItems.map((item, index) => (
                            item.type === 'link' ? (
                                <Link
                                    key={index}
                                    to={item.href}
                                    className="relative px-4 py-2 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white font-medium transition-all duration-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 group"
                                >
                                    {item.name}
                                    <div className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-slate-900 dark:bg-white transition-all duration-300 group-hover:w-3/4 transform -translate-x-1/2"></div>
                                </Link>
                            ) : (
                                <a
                                    key={index}
                                    href={item.href}
                                    className="relative px-4 py-2 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white font-medium transition-all duration-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 group"
                                >
                                    {item.name}
                                    <div className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-slate-900 dark:bg-white transition-all duration-300 group-hover:w-3/4 transform -translate-x-1/2"></div>
                                </a>
                            )
                        ))}
                        
                        {/* Dark Mode Toggle */}
                        <button
                            onClick={toggleTheme}
                            className="ml-4 p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors duration-300 focus:outline-none"
                            aria-label="Toggle dark mode"
                        >
                            {isDarkMode ? (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            ) : (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                </svg>
                            )}
                        </button>
                    </div>

                    {/* Mobile controls */}
                    <div className="lg:hidden flex items-center space-x-4">
                        {/* Mobile Dark Mode Toggle */}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors duration-300 focus:outline-none"
                            aria-label="Toggle dark mode"
                        >
                            {isDarkMode ? (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            ) : (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                </svg>
                            )}
                        </button>
                        
                        <button
                            onClick={toggleMenu}
                            className="relative p-2 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white focus:outline-none transition-colors duration-300"
                            aria-label="Toggle menu"
                        >
                            <div className="w-6 h-6 flex flex-col justify-center items-center">
                                <span className={`block w-6 h-0.5 bg-current transition-all duration-300 ${
                                    isOpen ? 'rotate-45 translate-y-1.5' : ''
                                }`}></span>
                                <span className={`block w-6 h-0.5 bg-current mt-1 transition-all duration-300 ${
                                    isOpen ? 'opacity-0' : ''
                                }`}></span>
                                <span className={`block w-6 h-0.5 bg-current mt-1 transition-all duration-300 ${
                                    isOpen ? '-rotate-45 -translate-y-1.5' : ''
                                }`}></span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                    <div className="py-4 space-y-2 border-t border-slate-200 dark:border-slate-800">
                        {navItems.map((item, index) => (
                            item.type === 'link' ? (
                                <Link
                                    key={index}
                                    to={item.href}
                                    onClick={() => setIsOpen(false)}
                                    className="block px-4 py-3 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 font-medium transition-all duration-300 rounded-lg"
                                >
                                    {item.name}
                                </Link>
                            ) : (
                                <a
                                    key={index}
                                    href={item.href}
                                    onClick={() => setIsOpen(false)}
                                    className="block px-4 py-3 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 font-medium transition-all duration-300 rounded-lg"
                                >
                                    {item.name}
                                </a>
                            )
                        ))}
                    </div>
                </div>
            </div>
        </nav>
    );
}

export default NavbarLanding;